import { useEffect, useMemo, useState, useContext } from "react";
import { useForm } from "react-hook-form";
import StageCard from "./stage-card";
import Icon from "../icon";
import useEventCreateMutation from "../../hooks/mutations/useEventCreateMutation";
import useEventUpdateMutation from "../../hooks/mutations/useEventUpdateMutation";
import EventSurvey from "./event-survey";
import useFocusOnFirstError from "../../hooks/useFocusOnFirstError";
import UserContext from "../../context/user";
import StageDetail from "./stage-detail";
import { isStageAvailable } from "../../utils/event";

const EventContent = ({
  event,
  stages,
  appLang,
  labels,
  reservation,
  eventNavigation,
  setEventNavigation,
  setReservation,
}) => {
  const { userProfile } = useContext(UserContext);
  const [formData, setFormData] = useState({});
  const {
    register,
    handleSubmit,
    control,
    trigger,
    watch,
    setValue,
    reset,
    setFocus,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  useFocusOnFirstError(errors, setFocus);
  const formWatch = watch();

  useEffect(() => {
    reset(formData);
  }, [reset, formData]);

  useEffect(() => {
    if (reservation) {
      const requestFields = {
        email: reservation?.email,
        email_validation: reservation?.email,
        guest_name: reservation?.guestFullName,
        transfer: reservation?.transferNeeded,
        stage: `${reservation?.stage?.id}`,
      };
      setFormData((current) => ({ ...current, ...requestFields }));
    } else if (userProfile) {
      const initializedFields = {
        email: userProfile?.mail,
        email_validation: userProfile?.mail,
        ...(stages?.length === 1 ? { stage: stages?.[0]?.id } : {}),
      };
      setFormData((current) => ({ ...current, ...initializedFields }));
    }
  }, [userProfile, reservation, setFormData, stages]);

  const {
    mutateAsync: createRequest,
    isError: isCreateError,
    isLoading: isCreateLoading,
  } = useEventCreateMutation(setEventNavigation, setReservation, appLang);
  const {
    mutateAsync: updateRequest,
    isError: isUpdateError,
    isLoading: isUpdateLoading,
  } = useEventUpdateMutation(setEventNavigation, setReservation, appLang);

  const onSubmit = async (values) => {
    const stage = stages?.find((stage) => stage.id === Number(values?.stage));

    if (reservation?.id) {
      await updateRequest({
        eventId: event?.id,
        formData: values,
        stage,
      });
    } else {
      await createRequest({
        eventId: event?.id,
        formData: values,
        stage,
      });
    }
  };

  const selectedStage = useMemo(
    () => stages?.find((stage) => stage.id === Number(formWatch?.stage)),
    [stages, formWatch]
  );

  return eventNavigation?.url === "stage-detail" ? (
    <StageDetail
      stage={stages?.find((stage) => stage.id === Number(eventNavigation?.id))}
      labels={labels}
      appLang={appLang}
      errors={errors}
      touchedFields={touchedFields}
      dirtyFields={dirtyFields}
      register={register}
      trigger={trigger}
      formWatch={formWatch}
      control={control}
      eventId={event?.id}
      createRequest={createRequest}
      updateRequest={updateRequest}
      reservation={reservation}
      handleSubmit={handleSubmit}
      isLoading={isCreateLoading || isUpdateLoading}
      isError={isCreateError || isUpdateError}
    />
  ) : (
    <>
      <div className="wrapper wrapper-small page-content">
        {event?.banner?.thumbUrl && (
          <div className="survey-detail__cover-wrapper">
            <img
              className="survey-detail__cover"
              src={event?.banner?.thumbUrl}
              alt="Survey cover"
            />
          </div>
        )}
        <div className="app__content">
          <div
            className="survey-detail__description"
            dangerouslySetInnerHTML={{ __html: event?.description }}
          />
          {stages?.length > 1 && (
            <h2 className="survey-detail__section-title">
              {labels?.EVENT_SELECT_STAGE || "Select the stage you are interested in"}
            </h2>
          )}
          {stages?.length > 0 ? (
            <form onSubmit={handleSubmit(onSubmit)} className="event__stages" noValidate>
              {stages?.map((stage) => (
                <StageCard
                  key={stage.id}
                  stage={stage}
                  appLang={appLang}
                  labels={labels}
                  multiple={stages?.length > 1}
                  setEventNavigation={setEventNavigation}
                  setValue={setValue}
                  register={register}
                  selectedStage={formWatch?.stage}
                  trigger={trigger}
                  setFocus={setFocus}
                />
              ))}
              <EventSurvey
                stage={stages?.find((stage) => stage.id === Number(formWatch?.stage))}
                reservation={reservation}
                errors={errors}
                touchedFields={touchedFields}
                dirtyFields={dirtyFields}
                control={control}
                trigger={trigger}
                labels={labels}
                locale={appLang?.value}
                formWatch={formWatch}
                appLang={appLang}
                register={register}
                visible={!!formWatch?.stage}
              />
              <div className="form__buttons-wrapper">
                {(isCreateError || isUpdateError) && (
                  <div className="error text-center">An error occurred saving your request</div>
                )}
                {errors?.stage && (
                  <div className="error text-center">
                    <Icon name="error" />
                    <span>
                      {labels?.EVENT_SELECTED_STAGE_ERROR_MSG ||
                        "Before continuing you must choose a stage."}
                    </span>
                  </div>
                )}
                <div className="form__buttons form__buttons--center">
                  <button className="button primary">
                    {(isCreateLoading || isUpdateLoading) && <span className="loading-spinner" />}
                    {stages?.length === 1
                      ? isStageAvailable(selectedStage)
                        ? labels?.EVENT_BOOK_EVENT || "Book event"
                        : labels?.EVENT_SIGN_UP_WAITING_LIST || "Sign up to the waiting list"
                      : formWatch?.stage
                      ? isStageAvailable(selectedStage)
                        ? labels?.EVENT_CONFIRM || "Confirm"
                        : labels?.EVENT_SIGN_UP_WAITING_LIST || "Sign up to the waiting list"
                      : stages?.some((stage) => isStageAvailable(stage))
                      ? labels?.EVENT_CONFIRM || "Confirm"
                      : labels?.EVENT_SIGN_UP_WAITING_LIST || "Sign up to the waiting list"}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-center">
              {labels?.EVENT_NO_STAGES_AVAILABLE || "The initiative is closed"}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default EventContent;
